import { ApplicationConfig, provideAppInitializer, provideZoneChangeDetection } from '@angular/core';
import { MARKED_OPTIONS, provideMarkdown } from 'ngx-markdown';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { HttpBackend, HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { providePrimeNG } from 'primeng/config';
import { provideRouter, withRouterConfig } from '@angular/router';
import { routes } from './app.routes';
import { LogLevel, provideAuth, StsConfigLoader, withAppInitializerAuthCheck } from 'angular-auth-oidc-client';
import { TokenInterceptor } from './token.interceptor';
import { configFactory } from './shared/services/config-service';
import { ConfirmationService, MessageService } from 'primeng/api';
import { DialogService } from 'primeng/dynamicdialog';
import { ErrorInterceptor } from './shared/interceptors/error.interceptor';
import { authConfigLoaderFactory } from './core/oidc-auth.config';
import { definePreset } from "@primeng/themes";
import Aura from '@primeng/themes/aura';
import { provideTablerIcons } from 'angular-tabler-icons';
import { NECTA_TABLER_ICONS } from './shared/icons/necta-icons';

// --- necta-Orange (Primär / Identität) -------------------------------------
const nectaOrange = {
  50:  '#fff1ec',
  100: '#ffe0d6',
  200: '#ffc1ad',
  300: '#ff9173',
  400: '#ff6238',
  500: '#ff3b00', // --necta-orange-500  (Marken-Anker)
  600: '#e82f00', // --necta-orange-600
  700: '#c9280a', // --necta-orange-700
  800: '#a31f12',
  900: '#821119', // --necta-orange-900
  950: '#4d0a0f'
};

// --- Warme Surface-Rampe (Struktur) — 1:1 aus tokens/colors.css ------------
const nectaSurfaceWarm = {
  0:   '#ffffff',
  50:  '#fbf9fc',
  100: '#f5f3f6',
  200: '#e9e6ea',
  300: '#d7d3d9',
  400: '#a6a1a7',
  500: '#757077',
  600: '#575259',
  700: '#433f45',
  800: '#2b272c',
  900: '#1a171b',
  950: '#0b080b'
};

const Noir = definePreset(Aura, {
  primitive: {
    nectaOrange,
    nectaBlue: {
      50:  '#f0f9ff',
      100: '#dcf1fa',
      300: '#29abe2',
      500: '#387ef7',
      700: '#1d6df5'
    }
  },

  semantic: {
    primary: nectaOrange,

    // Fokus-Ring = necta-Blau (funktional), nicht Orange.
    focusRing: {
      width: '0.2rem',
      style: 'solid',
      color: 'rgba(56, 126, 247, 0.35)',
      offset: '0'
    },

    colorScheme: {
      light: {
        primary: {
          color: '{primary.500}',
          contrastColor: '#ffffff',
          hoverColor: '{primary.600}',
          activeColor: '{primary.700}'
        },
        highlight: {
          background: '#eff6ff',
          focusBackground: '#dbeafe',
          color: '#1d4ed8',
          focusColor: '#1d4ed8'
        },
        surface: nectaSurfaceWarm
      },
      dark: {
        // Dark Mode (.app-dark) ist im DS nicht spezifiziert — Aura-Defaults
        // mit necta-Orange als primary belassen, bis es ein Dark-Spec gibt.
        primary: {
          color: '{primary.400}',
          contrastColor: '{surface.950}',
          hoverColor: '{primary.300}',
          activeColor: '{primary.200}'
        },
        highlight: {
          background: 'color-mix(in srgb, {primary.400}, transparent 84%)',
          focusBackground: 'color-mix(in srgb, {primary.400}, transparent 76%)',
          color: 'rgba(255,255,255,.87)',
          focusColor: 'rgba(255,255,255,.87)'
        },
        surface: nectaSurfaceWarm
      }
    }
  }
});

export const appConfig: ApplicationConfig = {
  providers: [
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideAnimationsAsync(),
    // Registriert den kuratierten Tabler-Icon-Satz global für <i-tabler name="…">.
    provideTablerIcons(NECTA_TABLER_ICONS),
    MessageService,
    ConfirmationService,
    DialogService,
    provideHttpClient(withInterceptorsFromDi()),
    { provide: HTTP_INTERCEPTORS, useClass: TokenInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: ErrorInterceptor, multi: true },
    provideRouter(routes, withRouterConfig({ onSameUrlNavigation: 'reload' })),
    providePrimeNG({
        theme: {
            preset: Noir, 
            options: {
                darkModeSelector: '.app-dark',
            cssLayer: {
                name: 'primeng',
                order: 'theme, base, primeng'
      }
            }
        }
    }),

    // Konfiguration der OpenID Connect Authentifizierung mit angular-auth-oidc-client.
    // Die Konfiguration wird zur Laufzeit aus der config.<env>.json geladen (StsConfigHttpLoader),
    // damit ein Profilwechsel (z.B. Authority local↔staging) ohne Angular-Rebuild wirkt.
    provideAuth({
      loader: {
        provide: StsConfigLoader,
        useFactory: authConfigLoaderFactory,
        deps: [HttpBackend],
      },
    },
    // Prüft beim App-Start automatisch den Authentifizierungsstatus.
    withAppInitializerAuthCheck()),

    // Lädt Konfigurationseinstellungen (z. B. aus einer externen Datei) beim App-Start.
    provideAppInitializer(configFactory),

    // Markdown-Rendering für den Qassandra-Chat-Kanal.
    // `breaks: true` ist bewusst gesetzt: Das Modell trennt in seinen Anleitungen
    // Schritt-Titel und Erklärung mit einem einfachen Zeilenumbruch. Der marked-Default
    // (`breaks: false`) würde daraus ein Leerzeichen machen und beides zu einer Zeile
    // verkleben; auf die Markdown-Konvention „zwei Leerzeichen am Zeilenende" ist bei
    // LLM-Ausgaben kein Verlass. Im Chat-Kontext ist Zeilenumbruch = Zeilenumbruch ohnehin
    // das erwartete Verhalten.
    provideMarkdown({
      markedOptions: {
        provide: MARKED_OPTIONS,
        useValue: { gfm: true, breaks: true },
      },
    }),
  ]
};
